import { useMemo, useState } from 'react'
import * as stylex from '@stylexjs/stylex'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import ActivityToolbar, { type ActivityKindFilter } from '../../components/ActivityToolbar'
import Avatar from '../../components/Avatar'
import Button from '../../components/Button'
import Icon, { type IconName } from '../../components/Icon'
import {
  expenseBalanceDelta,
  formatMoney,
  formatTransactionDate,
  type Balance,
  type Expense,
  type Overview,
} from '../../lib/ledger'

const styles = stylex.create({
  page: { display: 'flex', flexDirection: 'column', gap: '24px' },
  back: { alignSelf: 'flex-start', fontSize: '13px', fontWeight: 600, color: '#55735d', ':hover': { color: '#397a4b' } },
  header: { position: 'relative', display: 'flex', flexDirection: 'column', gap: '18px', borderRadius: '1rem', borderWidth: 1, borderColor: '#e1e8df', backgroundColor: '#fff', padding: '24px', boxShadow: '0 4px 16px rgba(48,70,51,.035)' },
  identityRow: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '20px' },
  identity: { display: 'flex', minWidth: 0, alignItems: 'center', gap: '14px' },
  snapshotGrid: { display: 'grid', gap: '10px', '@media (min-width: 680px)': { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' } },
  snapshotCard: { borderRadius: '0.75rem', borderWidth: 1, borderColor: '#e2e9df', backgroundColor: '#fbfdfb', padding: '12px' },
  snapshotValue: { marginTop: '3px', fontSize: '18px', fontWeight: 700, color: '#365f40' },
  snapshotLabel: { fontSize: '11px', fontWeight: 600, color: '#718075' },
  notesCard: { borderRadius: '0.75rem', backgroundColor: '#f6f8f3', padding: '12px 14px', fontSize: '12px', lineHeight: 1.55, color: '#59685e' },
  groupRow: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '7px' },
  groupLabel: { marginRight: '2px', fontSize: '11px', fontWeight: 600, color: '#7d897f' },
  groupChip: { borderRadius: '9999px', borderWidth: 1, borderColor: '#dbe6d9', backgroundColor: '#fff', paddingInline: '9px', paddingBlock: '5px', fontSize: '11px', fontWeight: 600, color: '#55705c' },
  eyebrow: { fontSize: '12px', fontWeight: 600, color: '#5b9164' },
  title: { marginTop: '4px', fontSize: 'clamp(1.75rem,3.2vw,2.25rem)', fontWeight: 600, letterSpacing: '-.04em', color: '#202720' },
  email: { marginTop: '6px', fontSize: '13px', color: '#748077' },
  heroGrid: { display: 'grid', gap: '12px', '@media (min-width: 760px)': { gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))' } },
  balanceCard: { borderRadius: '0.875rem', borderWidth: 1, borderColor: '#dfe8dd', backgroundColor: '#f8fbf7', padding: '16px' },
  balanceLabel: { fontSize: '12px', fontWeight: 600, color: '#647267' },
  balanceAmount: { marginTop: '5px', fontSize: '24px', fontWeight: 700, letterSpacing: '-.035em', color: '#2f5638' },
  settledAmount: { color: '#59705f' },
  balanceMeta: { marginTop: '4px', fontSize: '11px', color: '#879087' },
  actions: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  actionIcon: { height: '16px', width: '16px' },
  section: { borderRadius: '1rem', borderWidth: 1, borderColor: '#e1e8df', backgroundColor: '#fff', padding: '20px', boxShadow: '0 4px 16px rgba(48,70,51,.035)' },
  sectionHeader: { display: 'flex', flexDirection: 'column', gap: '10px', '@media (min-width: 680px)': { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between' } },
  sectionTitle: { fontSize: '15px', fontWeight: 600, color: '#2c342d' },
  sectionCopy: { marginTop: '4px', fontSize: '12px', color: '#748077' },
  breakdownGrid: { marginTop: '16px', display: 'grid', gap: '10px', '@media (min-width: 700px)': { gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' } },
  breakdownCard: { borderRadius: '0.75rem', borderWidth: 1, borderColor: '#e2e9df', backgroundColor: '#fbfdfb', padding: '14px' },
  breakdownTitle: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', fontSize: '12px', fontWeight: 700, color: '#405147' },
  breakdownLine: { marginTop: '9px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', fontSize: '11px', color: '#748077' },
  breakdownTotal: { marginTop: '11px', borderTopWidth: 1, borderColor: '#e5ebe3', paddingTop: '10px', fontWeight: 700, color: '#395d42' },
  range: { display: 'flex', gap: '4px', borderRadius: '0.75rem', backgroundColor: '#edf3eb', padding: '4px' },
  rangeButton: { minHeight: '32px', minWidth: '42px', borderRadius: '0.5rem', paddingInline: '9px', fontSize: '11px', fontWeight: 600, color: '#68756c', ':hover': { backgroundColor: 'rgb(255 255 255 / 55%)' } },
  rangeActive: { backgroundColor: '#fff', color: '#397a4b', boxShadow: '0 1px 2px rgb(38 59 42 / 8%)' },
  chartControls: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' },
  currencySelector: { display: 'flex', flexWrap: 'wrap', gap: '4px', borderRadius: '0.75rem', backgroundColor: '#edf3eb', padding: '4px' },
  currencyOption: { display: 'flex', minHeight: '32px', alignItems: 'center', gap: '5px', borderRadius: '0.5rem', paddingInline: '9px', fontSize: '11px', fontWeight: 600, color: '#68756c', cursor: 'pointer', ':hover': { backgroundColor: 'rgb(255 255 255 / 55%)' }, ':focus-within': { outline: '2px solid #4b8d5c', outlineOffset: '1px' } },
  currencyActive: { backgroundColor: '#fff', color: '#397a4b', boxShadow: '0 1px 2px rgb(38 59 42 / 8%)' },
  currencyRadio: { margin: 0, accentColor: '#397a4b' },
  chartHeader: { marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', fontSize: '12px', fontWeight: 600, color: '#4b6351' },
  chart: { height: '190px', width: '100%' },
  empty: { paddingBlock: '28px', textAlign: 'center', fontSize: '13px', color: '#748077' },
  activitySection: { overflow: 'hidden', borderRadius: '1rem', borderWidth: 1, borderColor: '#e1e8df', backgroundColor: '#fff', boxShadow: '0 4px 16px rgba(48,70,51,.035)' },
  activityTop: { display: 'flex', flexDirection: 'column', gap: '14px', borderBottomWidth: 1, borderColor: '#edf0eb', padding: '20px' },
  activityTitleRow: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px' },
  count: { fontSize: '11px', fontWeight: 600, color: '#879087' },
  row: { display: 'grid', width: '100%', gridTemplateColumns: 'auto minmax(0,1fr) auto', alignItems: 'center', gap: '12px', borderBottomWidth: 1, borderColor: '#edf0eb', paddingInline: '20px', paddingBlock: '15px', textAlign: 'left', ':last-child': { borderBottomColor: 'transparent' }, ':hover': { backgroundColor: '#f8fbf7' } },
  iconWrap: { display: 'flex', height: '40px', width: '40px', alignItems: 'center', justifyContent: 'center', borderRadius: '0.75rem' },
  expenseIcon: { backgroundColor: '#edf6eb', color: '#468554' },
  settlementIcon: { backgroundColor: '#f5f0e7', color: '#997149' },
  rowIcon: { height: '20px', width: '20px' },
  rowMain: { minWidth: '0px' },
  rowTitle: { display: 'block', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', fontSize: '14px', fontWeight: 600, color: '#29312a' },
  rowMeta: { marginTop: '3px', display: 'block', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', fontSize: '12px', color: '#748077' },
  rowRight: { minWidth: '150px', textAlign: 'right' },
  delta: { display: 'block', fontSize: '13px', fontWeight: 700 },
  deltaPositive: { color: '#347a49' },
  deltaNegative: { color: '#9a6040' },
  deltaNeutral: { color: '#6f7971' },
  running: { marginTop: '3px', display: 'block', fontSize: '11px', color: '#879087' },
  loadMore: { display: 'flex', justifyContent: 'center', borderTopWidth: 1, borderColor: '#edf0eb', padding: '14px' },
  exchangeSection: { overflow: 'hidden', borderRadius: '1rem', borderWidth: 1, borderColor: '#e1e8df', backgroundColor: '#fff' },
  exchangeHeader: { borderBottomWidth: 1, borderColor: '#edf0eb', paddingInline: '20px', paddingBlock: '12px', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.08em', color: '#889188' },
  exchangeRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', borderBottomWidth: 1, borderColor: '#edf0eb', paddingInline: '20px', paddingBlock: '14px', ':last-child': { borderBottomColor: 'transparent' } },
  exchangeMain: { display: 'flex', minWidth: 0, alignItems: 'center', gap: '12px' },
  exchangeTitle: { fontSize: '13px', fontWeight: 600, color: '#334238' },
})

type Props = {
  data: Overview
  personID: string
  onBack: () => void
  onEditPerson?: () => void
  onOpenExpense?: (expenseID: string) => void
  onAddExpense?: () => void
  onRecordExchange?: (fromCurrency: string) => void
  onSettle?: (balance: Balance) => void
  onShare?: () => void
}

type Range = '1m' | '3m' | '1y' | 'all'
type ChartCurrency = 'AWG' | 'USD' | 'EUR'

const chartCurrencies: readonly ChartCurrency[] = ['AWG', 'USD', 'EUR']

type LedgerEvent = {
  id: string
  kind: 'expense' | 'settlement'
  title: string
  detail: string
  date: string
  timeSet?: boolean
  currency: string
  deltaCents: number
  runningCents: number
  expenseID?: string
}

function expenseIcon(description: string): { name: IconName; label: string } {
  const value = description.toLowerCase()
  if (/\b(rent|lease|mortgage|housing|apartment)\b/.test(value)) return { name: 'rent', label: 'Housing' }
  if (/\b(food|grocery|groceries|dinner|lunch|breakfast|restaurant|cafe|café|coffee|pizza|burger|subway|fruit|fruits|bakery|dining|snack|meal)\b/.test(value)) return { name: 'food', label: 'Food' }
  if (/\b(clothing|clothes|shirt|shirts|shoes|socks|pants|shein|zara|h&m|hm)\b/.test(value)) return { name: 'clothing', label: 'Clothing' }
  if (/\b(klm|hotel|flight|plane|airbnb|reschedule|trip|bus|train|travel)\b/.test(value)) return { name: 'travel', label: 'Travel' }
  if (/\b(parking|gas|fuel|car|vehicle|transport|taxi|uber)\b/.test(value)) return { name: 'transport', label: 'Transport' }
  if (/\b(gift|present)\b/.test(value)) return { name: 'gift', label: 'Gift' }
  if (/\b(casino|movie|cinema|game|ticket|concert|entertainment)\b/.test(value)) return { name: 'entertainment', label: 'Entertainment' }
  if (/\b(utility|utilities|electricity|water|internet|phone)\b/.test(value)) return { name: 'utilities', label: 'Utilities' }
  if (/\b(amazon|shopping|shop|store|walmart|furniture|deodorant)\b/.test(value)) return { name: 'shopping', label: 'Shopping' }
  return { name: 'receipt', label: 'Expense' }
}

function expenseDetail(expense: Expense, currentUserID: string, personID: string, personName: string): string {
  const payerAmounts = expense.payerAmounts ?? { [expense.paidById]: expense.amountCents }
  const youPaid = payerAmounts[currentUserID] ?? 0
  const personPaid = payerAmounts[personID] ?? 0
  if (youPaid > 0 && personPaid > 0) return `You and ${personName} both paid`
  if (youPaid > 0) return 'You paid'
  if (personPaid > 0) return `${personName} paid`
  return 'Shared expense'
}

function balanceSentence(amountCents: number, currency: string, personName: string): string {
  if (amountCents > 0) return `${personName} owes you ${formatMoney(amountCents, currency)}`
  if (amountCents < 0) return `You owe ${personName} ${formatMoney(Math.abs(amountCents), currency)}`
  return `Settled up in ${currency}`
}

function chartRangeStart(points: Array<{ date: string; value: number }>, range: Range): number {
  if (range === 'all' || points.length === 0) return Number.NEGATIVE_INFINITY
  const latest = Math.max(...points.map((point) => new Date(point.date).getTime()))
  const days = range === '1m' ? 30 : range === '3m' ? 90 : 365
  return latest - days * 24 * 60 * 60 * 1000
}

export default function PersonDetailPage({
  data,
  personID,
  onBack,
  onEditPerson,
  onOpenExpense,
  onAddExpense,
  onRecordExchange,
  onSettle,
  onShare,
}: Props) {
  const [visibleCount, setVisibleCount] = useState(50)
  const [query, setQuery] = useState('')
  const [kind, setKind] = useState<ActivityKindFilter>('all')
  const [range, setRange] = useState<Range>('all')
  const [chartCurrency, setChartCurrency] = useState<ChartCurrency>('AWG')
  const person = data.people.find((item) => item.id === personID)

  const model = useMemo(() => {
    if (!person) return null

    const expenses = data.expenses
      .filter((expense) => {
        const payerAmounts = expense.payerAmounts ?? { [expense.paidById]: expense.amountCents }
        const personInvolved = payerAmounts[personID] !== undefined || expense.participantIds.includes(personID)
        const currentUserInvolved = payerAmounts[data.currentUser.id] !== undefined || expense.participantIds.includes(data.currentUser.id)
        return personInvolved && currentUserInvolved
      })
      .sort((left, right) => new Date(right.occurredAt ?? right.createdAt).getTime() - new Date(left.occurredAt ?? left.createdAt).getTime())
    const settlements = data.settlements.filter((settlement) => settlement.personId === personID && settlement.method !== 'currency_exchange')
    const exchangeSettlementLegs = data.settlements.filter((settlement) => settlement.personId === personID && settlement.method === 'currency_exchange')
    const balances = data.balances.filter((balance) => balance.personId === personID)
    const exchanges = data.exchanges.filter((exchange) => exchange.personId === personID).sort((left, right) => new Date(right.occurredAt ?? right.createdAt).getTime() - new Date(left.occurredAt ?? left.createdAt).getTime())

    const rawEvents: Omit<LedgerEvent, 'runningCents'>[] = [
      ...expenses.map((expense) => {
        const deltaCents = expenseBalanceDelta(expense, data.currentUser.id, personID)
        return {
          id: `expense:${expense.id}`,
          kind: 'expense' as const,
          title: expense.description,
          detail: expenseDetail(expense, data.currentUser.id, personID, person.name),
          date: expense.occurredAt ?? expense.createdAt,
          timeSet: expense.occurredTimeSet,
          currency: expense.currency,
          deltaCents,
          expenseID: expense.id,
        }
      }),
      ...settlements.map((settlement) => ({
        id: `settlement:${settlement.id}`,
        kind: 'settlement' as const,
        title: settlement.direction === 'received' ? `Payment from ${person.name}` : `Payment to ${person.name}`,
        detail: [settlement.direction === 'received' ? `${person.name} paid you` : `You paid ${person.name}`, settlement.description].filter(Boolean).join(' · '),
        date: settlement.paidAt ?? settlement.createdAt,
        timeSet: settlement.paidTimeSet,
        currency: settlement.currency,
        deltaCents: settlement.direction === 'received' ? -settlement.amountCents : settlement.amountCents,
      })),
      ...exchangeSettlementLegs.map((settlement) => ({
        id: `exchange-leg:${settlement.id}`,
        kind: 'settlement' as const,
        title: 'Currency exchange',
        detail: settlement.direction === 'received' ? `${person.name} gave you ${settlement.currency}` : `You gave ${person.name} ${settlement.currency}`,
        date: settlement.paidAt ?? settlement.createdAt,
        timeSet: settlement.paidTimeSet,
        currency: settlement.currency,
        deltaCents: settlement.direction === 'received' ? -settlement.amountCents : settlement.amountCents,
      })),
    ].filter((event) => event.deltaCents !== 0 || event.kind === 'expense')

    const currencies = Array.from(new Set([
      ...rawEvents.map((event) => event.currency),
      ...balances.map((balance) => balance.currency),
    ])).sort()
    const currentByCurrency = new Map(balances.map((balance) => [balance.currency, balance.amountCents]))
    const sumByCurrency = new Map<string, number>()
    rawEvents.forEach((event) => sumByCurrency.set(event.currency, (sumByCurrency.get(event.currency) ?? 0) + event.deltaCents))
    const runningByCurrency = new Map<string, number>()
    currencies.forEach((currency) => runningByCurrency.set(currency, (currentByCurrency.get(currency) ?? 0) - (sumByCurrency.get(currency) ?? 0)))

    const ascending = [...rawEvents].sort((left, right) => new Date(left.date).getTime() - new Date(right.date).getTime())
    const withRunning = ascending.map((event) => {
      const runningCents = (runningByCurrency.get(event.currency) ?? 0) + event.deltaCents
      runningByCurrency.set(event.currency, runningCents)
      return { ...event, runningCents }
    })

    const charts = currencies.map((currency) => ({
      currency,
      points: withRunning
        .filter((event) => event.currency === currency && event.deltaCents !== 0)
        .map((event) => ({ date: event.date, value: event.runningCents })),
      current: currentByCurrency.get(currency) ?? 0,
    }))

    return {
      balances,
      charts,
      currencies,
      currentByCurrency,
      events: withRunning.reverse(),
      exchanges,
      expenses,
      settlements,
    }
  }, [data, person, personID])

  if (!person || !model) return null

  const normalizedQuery = query.trim().toLowerCase()
  const filteredEvents = model.events.filter((event) =>
    (kind === 'all' || event.kind === kind) &&
    (!normalizedQuery || `${event.title} ${event.detail} ${event.currency}`.toLowerCase().includes(normalizedQuery)),
  )
  const activeBalances = model.currencies.map((currency) => ({
    currency,
    amountCents: model.currentByCurrency.get(currency) ?? 0,
  }))
  const settleableBalances = activeBalances.filter((balance) => balance.amountCents !== 0)
  const groupsTogether = data.groups.filter((group) => group.memberIds.includes(personID))
  const breakdowns = model.currencies.map((currency) => {
    const expenseImpact = model.events.filter((event) => event.currency === currency && event.kind === 'expense').reduce((total, event) => total + event.deltaCents, 0)
    const paymentImpact = model.events.filter((event) => event.currency === currency && event.kind === 'settlement').reduce((total, event) => total + event.deltaCents, 0)
    return { currency, expenseImpact, paymentImpact, current: model.currentByCurrency.get(currency) ?? 0 }
  })
  const selectedChart = model.charts.find((chart) => chart.currency === chartCurrency)
  const selectedPoints = selectedChart?.points ?? []
  const chartStart = chartRangeStart(selectedPoints, range)
  const visiblePoints = selectedPoints.filter((point) => new Date(point.date).getTime() >= chartStart)

  return (
    <div {...stylex.props(styles.page)}>
      <button {...stylex.props(styles.back)} onClick={onBack} type="button">← All people</button>

      <header {...stylex.props(styles.header)}>
        <div {...stylex.props(styles.identityRow)}>
          <div {...stylex.props(styles.identity)}>
            <Avatar person={person} size={58} />
            <div>
              <p {...stylex.props(styles.eyebrow)}>Shared ledger</p>
              <h1 {...stylex.props(styles.title)}>{person.name}</h1>
              <p {...stylex.props(styles.email)}>{person.email || 'Every expense and payment between you.'}</p>
            </div>
          </div>
          <Button onClick={onEditPerson} size="sm" variant="secondary">Edit</Button>
        </div>

        <div {...stylex.props(styles.heroGrid)}>
          {activeBalances.length === 0 ? (
            <div {...stylex.props(styles.balanceCard)}>
              <p {...stylex.props(styles.balanceLabel)}>Current balance</p>
              <p {...stylex.props(styles.balanceAmount, styles.settledAmount)}>Settled up</p>
              <p {...stylex.props(styles.balanceMeta)}>No open balance between you and {person.name}.</p>
            </div>
          ) : activeBalances.map((balance) => (
            <div {...stylex.props(styles.balanceCard)} key={balance.currency}>
              <p {...stylex.props(styles.balanceLabel)}>
                {balance.amountCents > 0 ? `${person.name} owes you` : balance.amountCents < 0 ? `You owe ${person.name}` : 'Settled up'}
              </p>
              <p {...stylex.props(styles.balanceAmount, balance.amountCents === 0 && styles.settledAmount)}>
                {formatMoney(Math.abs(balance.amountCents), balance.currency)}
              </p>
              <p {...stylex.props(styles.balanceMeta)}>
                {model.expenses.filter((expense) => expense.currency === balance.currency).length} expenses · {model.settlements.filter((settlement) => settlement.currency === balance.currency).length} payments
              </p>
            </div>
          ))}
        </div>

        <div {...stylex.props(styles.snapshotGrid)}>
          <div {...stylex.props(styles.snapshotCard)}><p {...stylex.props(styles.snapshotLabel)}>Shared expenses</p><p {...stylex.props(styles.snapshotValue)}>{model.expenses.length}</p></div>
          <div {...stylex.props(styles.snapshotCard)}><p {...stylex.props(styles.snapshotLabel)}>Payments</p><p {...stylex.props(styles.snapshotValue)}>{model.settlements.length}</p></div>
          <div {...stylex.props(styles.snapshotCard)}><p {...stylex.props(styles.snapshotLabel)}>Groups together</p><p {...stylex.props(styles.snapshotValue)}>{groupsTogether.length}</p></div>
        </div>
        {person.notes && <div {...stylex.props(styles.notesCard)}><strong>Private note:</strong> {person.notes}</div>}
        {groupsTogether.length > 0 && <div {...stylex.props(styles.groupRow)}><span {...stylex.props(styles.groupLabel)}>Together in</span>{groupsTogether.map((group) => <span {...stylex.props(styles.groupChip)} key={group.id}>{group.name}</span>)}</div>}

        <div {...stylex.props(styles.actions)}>
          {settleableBalances.map((balance) => (
            <Button key={balance.currency} onClick={() => onSettle?.({ personId: personID, amountCents: balance.amountCents, currency: balance.currency })}>
              <Icon {...stylex.props(styles.actionIcon)} name="settle" />
              {settleableBalances.length === 1 ? 'Settle' : `Settle ${balance.currency}`}
            </Button>
          ))}
          <Button onClick={onAddExpense} variant={settleableBalances.length > 0 ? 'secondary' : 'primary'}>
            <Icon {...stylex.props(styles.actionIcon)} name="plus" />Add expense
          </Button>
          <Button onClick={onShare} variant="secondary">Share</Button>
        </div>
      </header>

      <section {...stylex.props(styles.section)}>
        <div {...stylex.props(styles.sectionHeader)}><div><h2 {...stylex.props(styles.sectionTitle)}>Why this balance?</h2><p {...stylex.props(styles.sectionCopy)}>A compact breakdown of recorded expenses and payments between you.</p></div></div>
        <div {...stylex.props(styles.breakdownGrid)}>
          {breakdowns.map((item) => (
            <div {...stylex.props(styles.breakdownCard)} key={item.currency}>
              <div {...stylex.props(styles.breakdownTitle)}><span>{item.currency}</span><span>{balanceSentence(item.current, item.currency, person.name)}</span></div>
              <div {...stylex.props(styles.breakdownLine)}><span>Expense impact</span><strong>{item.expenseImpact >= 0 ? '+' : '−'}{formatMoney(Math.abs(item.expenseImpact), item.currency)}</strong></div>
              <div {...stylex.props(styles.breakdownLine)}><span>Payment impact</span><strong>{item.paymentImpact >= 0 ? '+' : '−'}{formatMoney(Math.abs(item.paymentImpact), item.currency)}</strong></div>
              <div {...stylex.props(styles.breakdownLine, styles.breakdownTotal)}><span>Current balance</span><strong>{formatMoney(Math.abs(item.current), item.currency)}</strong></div>
            </div>
          ))}
        </div>
      </section>

      <section {...stylex.props(styles.section)}>
        <div {...stylex.props(styles.sectionHeader)}>
          <div>
            <h2 {...stylex.props(styles.sectionTitle)}>Balance history</h2>
            <p {...stylex.props(styles.sectionCopy)}>See exactly how expenses and payments changed what is owed.</p>
          </div>
          <div {...stylex.props(styles.chartControls)}>
            <div aria-label="Balance history currency" {...stylex.props(styles.currencySelector)} role="radiogroup">
              {chartCurrencies.map((currency) => (
                <label {...stylex.props(styles.currencyOption, chartCurrency === currency && styles.currencyActive)} key={currency}>
                  <input checked={chartCurrency === currency} {...stylex.props(styles.currencyRadio)} name="balance-history-currency" onChange={() => setChartCurrency(currency)} type="radio" value={currency} />
                  {currency}
                </label>
              ))}
            </div>
            <div {...stylex.props(styles.range)}>
              {(['1m', '3m', '1y', 'all'] as const).map((value) => (
                <button aria-pressed={range === value} {...stylex.props(styles.rangeButton, range === value && styles.rangeActive)} key={value} onClick={() => setRange(value)} type="button">
                  {value === 'all' ? 'All' : value.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>
        {visiblePoints.length === 0 ? <p {...stylex.props(styles.empty)}>No balance history in {chartCurrency} yet.</p> : (
          <div>
            <div {...stylex.props(styles.chartHeader)}><span>{chartCurrency}</span><span>{formatMoney(selectedChart?.current ?? 0, chartCurrency)}</span></div>
            <div {...stylex.props(styles.chart)}>
              <ResponsiveContainer height="100%" width="100%">
                <LineChart data={visiblePoints} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke="#dce9da" strokeDasharray="3 3" vertical={false} />
                  <XAxis axisLine={false} dataKey="date" minTickGap={28} tick={{ fill: '#7b887d', fontSize: 9 }} tickFormatter={(value) => new Date(value).toLocaleDateString(undefined, { month: 'short', year: '2-digit' })} tickLine={false} />
                  <YAxis axisLine={false} tick={{ fill: '#7b887d', fontSize: 9 }} tickFormatter={(value) => formatMoney(Number(value), chartCurrency)} tickLine={false} width={54} />
                  <Tooltip contentStyle={{ background: '#fffefa', border: '1px solid #d9e6d7', borderRadius: 10, boxShadow: '0 6px 18px rgba(48,70,51,.12)', fontSize: 12 }} formatter={(value) => formatMoney(Number(value), chartCurrency)} labelFormatter={(value) => new Date(String(value)).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })} />
                  <Line activeDot={{ r: 4, strokeWidth: 2 }} dataKey="value" dot={false} isAnimationActive={false} stroke="#438b57" strokeWidth={2} type="linear" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </section>

      <section {...stylex.props(styles.activitySection)}>
        <div {...stylex.props(styles.activityTop)}>
          <div {...stylex.props(styles.activityTitleRow)}>
            <div><h2 {...stylex.props(styles.sectionTitle)}>Activity</h2><p {...stylex.props(styles.sectionCopy)}>A running ledger of why the balance is what it is.</p></div>
            <span {...stylex.props(styles.count)}>{filteredEvents.length} entries</span>
          </div>
          <ActivityToolbar kind={kind} onKindChange={setKind} onQueryChange={setQuery} placeholder="Search expenses or payments" query={query} />
        </div>
        {filteredEvents.length === 0 ? <p {...stylex.props(styles.empty)}>No matching activity.</p> : filteredEvents.slice(0, visibleCount).map((event) => {
          const category = event.kind === 'expense' ? expenseIcon(event.title) : { name: 'settle' as IconName, label: 'Payment' }
          const sign = event.deltaCents > 0 ? '+' : event.deltaCents < 0 ? '−' : ''
          return (
            <button {...stylex.props(styles.row)} key={event.id} onClick={() => event.expenseID && onOpenExpense?.(event.expenseID)} type="button">
              <span {...stylex.props(styles.iconWrap, event.kind === 'expense' ? styles.expenseIcon : styles.settlementIcon)} title={category.label}><Icon {...stylex.props(styles.rowIcon)} name={category.name} /></span>
              <span {...stylex.props(styles.rowMain)}><span {...stylex.props(styles.rowTitle)}>{event.title}</span><span {...stylex.props(styles.rowMeta)}>{event.detail} · {formatTransactionDate(event.date, Boolean(event.timeSet))}</span></span>
              <span {...stylex.props(styles.rowRight)}>
                <span {...stylex.props(styles.delta, event.deltaCents > 0 ? styles.deltaPositive : event.deltaCents < 0 ? styles.deltaNegative : styles.deltaNeutral)}>{sign}{formatMoney(Math.abs(event.deltaCents), event.currency)}</span>
                <span {...stylex.props(styles.running)}>{balanceSentence(event.runningCents, event.currency, person.name)}</span>
              </span>
            </button>
          )
        })}
        {filteredEvents.length > visibleCount && <div {...stylex.props(styles.loadMore)}><Button onClick={() => setVisibleCount((count) => count + 50)} size="sm" variant="secondary">Load 50 more</Button></div>}
      </section>

      {model.exchanges.length > 0 && (
        <section {...stylex.props(styles.exchangeSection)}>
          <div {...stylex.props(styles.exchangeHeader)}>Currency exchanges ({model.exchanges.length})</div>
          {model.exchanges.map((exchange) => (
            <div {...stylex.props(styles.exchangeRow)} key={exchange.id}>
              <div {...stylex.props(styles.exchangeMain)}><span {...stylex.props(styles.iconWrap, styles.settlementIcon)}><Icon {...stylex.props(styles.rowIcon)} name="settle" /></span><div><p {...stylex.props(styles.exchangeTitle)}>{formatMoney(exchange.fromAmountCents, exchange.fromCurrency)} → {formatMoney(exchange.toAmountCents, exchange.toCurrency)}</p><p {...stylex.props(styles.rowMeta)}>{new Date(exchange.occurredAt ?? exchange.createdAt).toLocaleDateString()} · 1 {exchange.fromCurrency} = {(exchange.rateBps / 10000).toFixed(2)} {exchange.toCurrency}</p></div></div>
              <Button onClick={() => onRecordExchange?.(exchange.fromCurrency)} size="sm" variant="secondary">Exchange again</Button>
            </div>
          ))}
        </section>
      )}
    </div>
  )
}
